import React, { useState, useCallback, useRef } from 'react';
import { getImageUrl } from '../../utils/imageUtils';

interface UserAvatarProps {
  user: {
    firstName?: string;
    lastName?: string;
    avatarUrl?: string | null;
  };
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

const sizeClasses = {
  sm: 'w-8 h-8 text-xs',
  md: 'w-14 h-14 text-xl',
  lg: 'w-20 h-20 text-2xl',
};

export const UserAvatar: React.FC<UserAvatarProps> = ({ user, size = 'md', className = '' }) => {
  const [imgLoaded, setImgLoaded] = useState(false);
  const [imgError, setImgError] = useState(false);
  const prevSrcRef = useRef<string | null>(null);

  const avatarSrc = user.avatarUrl ? getImageUrl(user.avatarUrl) : null;

  // URL-Wechsel erkennen und Status zurücksetzen (ohne useEffect)
  if (avatarSrc !== prevSrcRef.current) {
    prevSrcRef.current = avatarSrc;
    setImgLoaded(false);
    setImgError(false);
  }

  // Ref-Callback: Prüft ob das Bild bereits aus dem Cache geladen ist
  const imgRefCallback = useCallback((img: HTMLImageElement | null) => {
    if (img?.complete && img.naturalHeight > 0) {
      setImgLoaded(true);
    }
  }, []);

  const initials = `${user.firstName?.charAt(0) || ''}${user.lastName?.charAt(0) || ''}`;

  // Skeleton-Platzhalter während das Bild lädt
  if (avatarSrc && !imgError) {
    return (
      <div className={`${sizeClasses[size]} rounded-full shrink-0 relative ${className}`}>
        {/* Skeleton-Animation, bis das Bild geladen ist */}
        {!imgLoaded && (
          <div className="absolute inset-0 rounded-full bg-gray-200 dark:bg-gray-700 animate-pulse" />
        )}
        <img
          ref={imgRefCallback}
          src={avatarSrc}
          alt={initials}
          className={`w-full h-full rounded-full object-cover transition-opacity duration-200 ${imgLoaded ? 'opacity-100' : 'opacity-0'}`}
          onLoad={() => setImgLoaded(true)}
          onError={() => setImgError(true)}
        />
      </div>
    );
  }

  // Fallback: Initialen (nur wenn kein Avatar vorhanden oder Ladefehler)
  return (
    <div className={`${sizeClasses[size]} rounded-full flex items-center justify-center bg-amber-500 text-white font-bold shrink-0 ${className}`}>
      {initials}
    </div>
  );
};
